import React, { useState } from "react";
import { useOfferUrls, useCreateOfferUrl, useDeleteOfferUrl, useOfferAffiliates } from "@/hooks/use-offers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/Card";
import { ExternalLink, Plus, Trash2, Globe, Upload, Users, Pencil, Check, X as XIcon } from "lucide-react";
import { MacroPicker } from "@/components/MacroPicker";

interface Props {
  offerId: string;
}

export function OfferUrlsTab({ offerId }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useOfferUrls(offerId);
  const createMutation = useCreateOfferUrl(offerId);
  const deleteMutation = useDeleteOfferUrl(offerId);
  const { data: affData } = useOfferAffiliates(offerId);
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", previewUrl: "", isApplySpecificAffiliates: false, affiliateIds: "" });
  const [bulkText, setBulkText] = useState("");
  const [error, setError] = useState("");

  const urls = data?.data ?? [];
  const assignedAffiliates = affData?.data ?? [];
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: "", url: "", previewUrl: "" });

  const updateMutation = useMutation({
    mutationFn: ({ urlId, data }: { urlId: number; data: Record<string, unknown> }) =>
      api.patch(`/v1/offers/${offerId}/urls/${urlId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offer-urls", offerId] });
      setEditingId(null);
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (urls: Array<{ name: string; url: string }>) =>
      api.post(`/v1/offers/${offerId}/urls/bulk`, { urls }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offer-urls", offerId] });
      setBulkText("");
      setShowBulk(false);
    },
  });

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await createMutation.mutateAsync({
        ...form,
        affiliateIds: form.isApplySpecificAffiliates ? form.affiliateIds : "",
      } as any);
      setForm({ name: "", url: "", previewUrl: "", isApplySpecificAffiliates: false, affiliateIds: "" });
      setShowForm(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create URL");
    }
  }

  function handleBulkCreate() {
    const lines = bulkText.trim().split("\n").filter(Boolean);
    const urls = lines.map((line, i) => {
      const parts = line.split(",").map((s) => s.trim());
      return {
        name: parts[0] || `URL ${i + 1}`,
        url: parts[1] || parts[0],
      };
    });
    if (urls.length === 0) return;
    bulkMutation.mutate(urls);
  }

  async function handleDelete(urlId: number, urlName: string) {
    if (!window.confirm(`Delete "${urlName}"?`)) return;
    await deleteMutation.mutateAsync(urlId);
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-gray-100 rounded-xl" />
        <div className="h-24 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Offer URLs</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Additional landing page URLs for this offer. Affiliates can use specific URLs in their tracking links.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowBulk(!showBulk); setShowForm(false); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            Bulk Add
          </button>
          <button
            onClick={() => { setShowForm(!showForm); setShowBulk(false); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add URL
          </button>
        </div>
      </div>

      {showBulk && (
        <Card>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Paste URLs (one per line, format: <code className="text-xs bg-gray-100 px-1 rounded">Name, URL</code>)
              </label>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={5}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder={"Landing Page A, https://example.com/lp-a\nLanding Page B, https://example.com/lp-b"}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulk(false)}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCreate}
                disabled={bulkMutation.isPending || !bulkText.trim()}
                className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {bulkMutation.isPending ? "Adding..." : `Add ${bulkText.trim().split("\n").filter(Boolean).length} URLs`}
              </button>
            </div>
          </div>
        </Card>
      )}

      {showForm && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-3">
            {error && (
              <div className="bg-red-50 text-red-700 text-xs p-2 rounded-lg border border-red-200">
                {error}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="e.g., Landing Page B"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700">URL *</label>
                <MacroPicker onInsert={(macro) => setForm((f) => ({ ...f, url: f.url + macro }))} />
              </div>
              <input
                type="text"
                required
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="https://advertiser.com/landing-b?tid={transaction_id}"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Preview URL</label>
              <input
                type="url"
                value={form.previewUrl}
                onChange={(e) => setForm((f) => ({ ...f, previewUrl: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder="https://advertiser.com/preview-b"
              />
            </div>

            {/* Per-affiliate assignment */}
            <div className="border-t border-gray-100 pt-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isApplySpecificAffiliates}
                  onChange={(e) => setForm((f) => ({ ...f, isApplySpecificAffiliates: e.target.checked }))}
                  className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs font-medium text-gray-700">Assign to specific affiliates only</span>
              </label>

              {form.isApplySpecificAffiliates && (
                <div className="mt-2 ml-6">
                  <p className="text-xs text-gray-500 mb-2">Select affiliates that should use this URL:</p>
                  <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {assignedAffiliates.length === 0 ? (
                      <p className="text-xs text-gray-400">No affiliates assigned to this offer yet.</p>
                    ) : (
                      assignedAffiliates.map((aff: { affiliateId: number; affiliateName: string }) => {
                        const currentIds = form.affiliateIds ? form.affiliateIds.split(",").map(Number) : [];
                        const checked = currentIds.includes(aff.affiliateId);
                        return (
                          <label key={aff.affiliateId} className="flex items-center gap-2 text-xs text-gray-700 hover:bg-gray-50 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                let ids = currentIds.filter(Boolean);
                                if (e.target.checked) {
                                  ids.push(aff.affiliateId);
                                } else {
                                  ids = ids.filter((id) => id !== aff.affiliateId);
                                }
                                setForm((f) => ({ ...f, affiliateIds: ids.join(",") }));
                              }}
                              className="rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                            />
                            #{aff.affiliateId} - {aff.affiliateName}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setError(""); }}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {createMutation.isPending ? "Adding..." : "Add URL"}
              </button>
            </div>
          </form>
        </Card>
      )}

      {urls.length === 0 && !showForm && !showBulk ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl">
          <Globe className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No additional URLs configured</p>
          <p className="text-xs text-gray-400 mt-1">The default destination URL will be used for all traffic</p>
        </div>
      ) : (
        <div className="space-y-2">
          {urls.map((url: { id: number; name: string; url: string; previewUrl: string; isApplySpecificAffiliates?: boolean; affiliateIds?: string; createdAt: string }) => (
            <div
              key={url.id}
              className="bg-white border border-gray-200 rounded-lg px-4 py-3"
            >
              {editingId === url.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
                    <div className="flex gap-1">
                      <input type="text" value={editForm.url} onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))} placeholder="URL" className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-900" />
                      <MacroPicker onInsert={(macro) => setEditForm((f) => ({ ...f, url: f.url + macro }))} />
                    </div>
                  </div>
                  <input type="url" value={editForm.previewUrl} onChange={(e) => setEditForm((f) => ({ ...f, previewUrl: e.target.value }))} placeholder="Preview URL (optional)" className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => setEditingId(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><XIcon className="w-4 h-4" /></button>
                    <button onClick={() => updateMutation.mutate({ urlId: url.id, data: editForm })} disabled={updateMutation.isPending} className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
                      <Check className="w-3.5 h-3.5" /> Save
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{url.name}</span>
                      <span className="text-xs text-gray-400 font-mono">ID: {url.id}</span>
                      {url.isApplySpecificAffiliates && (
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                          <Users className="w-3 h-3" /> Per-affiliate
                        </span>
                      )}
                    </div>
                    <a href={url.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-0.5 break-all">
                      {url.url} <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button onClick={() => { setEditingId(url.id); setEditForm({ name: url.name, url: url.url, previewUrl: url.previewUrl || "" }); }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(url.id, url.name)} disabled={deleteMutation.isPending} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
